import {Truncate} from '@primer/react'
import {SkeletonText} from '@primer/react/experimental'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import type {SidebarContributorsProps} from '../types'
import {SidebarSection} from './SidebarSection'
import {SidebarLink} from './SidebarLink'
import {getItemsToShow} from '../utils'
import styles from './SidebarContributors.module.css'

const MAX_COUNT = 14

/** Renders the Contributors section of the repository sidebar. */
export function SidebarContributors({contributorCount, contributors, contributorsPath}: SidebarContributorsProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  if (contributorCount === 0) {
    return (
      <SidebarSection title="Contributors">
        <div className={styles.emptyState}>No contributors</div>
      </SidebarSection>
    )
  }

  const itemsToShow = getItemsToShow(contributorCount, MAX_COUNT)
  const displayed = contributors.slice(0, itemsToShow)
  const showGrid = itemsToShow > 4
  const moreCount = contributorCount - itemsToShow

  return (
    <SidebarSection
      title="Contributors"
      href={contributorsPath}
      count={contributorCount}
      moreCount={moreCount}
      moreLabel={`contributor${moreCount === 1 ? '' : 's'}`}
    >
      {showGrid ? (
        <ul className={styles.avatarGrid}>
          {displayed.map(c => (
            <li key={`contrib-${c.login}`} className={styles.avatarGridItem}>
              <SidebarLink
                to={c.profilePath}
                analyticsLabel="section:contributors"
                {...getHovercardAttributesForType('user', {login: c.login})}
              >
                <GitHubAvatar src={c.avatarUrl} alt={`@${c.login}`} size={32} />
              </SidebarLink>
            </li>
          ))}
        </ul>
      ) : (
        <ul className={styles.avatarList}>
          {displayed.map(c => (
            <li key={`contrib-${c.login}`} className={styles.avatarListItem}>
              <SidebarLink
                to={c.profilePath}
                className={styles.avatarLink}
                analyticsLabel="section:contributors"
                {...getHovercardAttributesForType('user', {login: c.login})}
              >
                <GitHubAvatar src={c.avatarUrl} alt={`@${c.login}`} size={32} />
              </SidebarLink>
              <Truncate
                as="span"
                className={styles.contributorName}
                title={`${c.login}${c.profileName ? ` ${c.profileName}` : ''}`}
                maxWidth="none"
              >
                <SidebarLink
                  to={c.profilePath}
                  className={styles.nameLink}
                  analyticsLabel="section:contributors"
                  {...getHovercardAttributesForType('user', {login: c.login})}
                >
                  <strong>{c.login}</strong>
                  {c.profileName && <span className={styles.profileName}>{c.profileName}</span>}
                </SidebarLink>
              </Truncate>
            </li>
          ))}
        </ul>
      )}
    </SidebarSection>
  )
}

export function SidebarContributorsSkeleton() {
  return (
    <SidebarSection title="Contributors">
      <SkeletonText />
    </SidebarSection>
  )
}
